import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SelectBillingByRealEstateDialogComponent } from '../components/billings/select-billing-by-realestate-dialog/select-billing-by-real-estate-dialog.component';
import { Router } from '@angular/router';
import { SelectTenantUnitByRealEstateDialogComponent } from '../components/select-tenantunit-by-realestate-dialog/select-tenant-unit-by-real-estate-dialog.component';
import { RoleModelService } from '../components/tenant-unit-detail-legacy/roles-view/role-model.service';
import { PersonEditorDialogComponent } from '../components/tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-dialog.component';
import {
	PersonEditorDialogData,
	PersonEditorEntryContext,
} from '../components/tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-dialog.types';
import { EditorTabContext, PersonEditorDataFactory } from '../components/tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-data.factory';
import { PropertyManagementRightsService } from '../components/tenant-unit-detail-legacy/roles-view/property-management-rights.service';
import { UviReceptionRights, UviReceptionRightsService } from '../components/tenant-unit-detail-legacy/roles-view/uvi-reception-rights.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { RealEstate, RealEstateContact, RealEstateService, TenantUnitDetail } from '@eadsysteme/ewsrealestateclient-v3';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { RoleType, UiRoleType } from './RoleModel';
import { SessionService } from './session.service';
import { BillingHelper } from './BillingHelper';
import SyncLevelEnum = RealEstate.SyncLevelEnum;

@Injectable({
	providedIn: 'root',
})
export class QuickActions {
	private readonly destroyRef = inject(DestroyRef);
	private readonly billingHelper = inject(BillingHelper);
	private readonly roleModelService = inject(RoleModelService);
	private readonly editorDataFactory = inject(PersonEditorDataFactory);
	private readonly uviReceptionRightsService = inject(UviReceptionRightsService);
	private readonly propertyManagementRightsService = inject(PropertyManagementRightsService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly sessionService = inject(SessionService);
	private readonly translocoService = inject(TranslocoService);
	private readonly dialog = inject(MatDialog);
	private readonly router = inject(Router);

	openNewCostDialog() {
		const matDialogRef = this.dialog.open(SelectBillingByRealEstateDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			width: '500px',
		});
		matDialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					if (result?.saved && result?.routeToNavigate) {
						this.router.navigate([result.routeToNavigate]);
					}
				},
			});
	}

	openSelectRealEstateAndTenantUnitDialog(
		successCallback: (realEstateContact: RealEstateContact, tenantUnit: TenantUnitDetail) => void,
		errorCallback?: (error: any) => void
	) {
		const dialogRef = this.dialog.open(SelectTenantUnitByRealEstateDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			width: '500px',
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (selectTenantUnitDialogResult) => {
					if (
						selectTenantUnitDialogResult?.saved &&
						selectTenantUnitDialogResult?.tenantUnit?.id &&
						selectTenantUnitDialogResult?.realEstateContact?.realEstateId
					) {
						successCallback(selectTenantUnitDialogResult.realEstateContact, selectTenantUnitDialogResult.tenantUnit);
					}
				},
				error: (error) => {
					if (errorCallback) {
						errorCallback(error);
					}
				},
			});
	}

	openTenantChange() {
		this.openSelectRealEstateAndTenantUnitDialog((realEstateContact, tenantUnit) => this.openPersonEditor('resident', realEstateContact, tenantUnit));
	}

	openVacancyChange() {
		this.openSelectRealEstateAndTenantUnitDialog((realEstateContact, tenantUnit) => this.openPersonEditor('vacancy', realEstateContact, tenantUnit));
	}

	/**
	 * Öffnet denselben Personen-/Rollen-Editor wie der Personen-Tab der Nutzeinheit — der Schnellzugriff
	 * darf keinen abweichenden Dialog zeigen. Die Liegenschaft wird nachgeladen, weil erst ihr `syncLevel`
	 * entscheidet, ob die Mieter-Rolle angeboten wird (der Auswahl-Dialog liefert nur den Kontakt).
	 */
	private openPersonEditor(entryContext: PersonEditorEntryContext, realEstateContact: RealEstateContact, tenantUnit: TenantUnitDetail): void {
		forkJoin([
			this.loadRealEstate(realEstateContact),
			this.uviReceptionRightsService.resolve(this.areaOf(realEstateContact), realEstateContact.realEstateId),
			this.propertyManagementRightsService.resolve(),
		])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([realEstate, uviReceptionRights, isPropertyManagement]) =>
				this.openPersonEditorDialog(entryContext, realEstateContact, tenantUnit, realEstate, uviReceptionRights, isPropertyManagement)
			);
	}

	/** Ohne Liegenschaft öffnet der Editor trotzdem — dann eben ohne `syncLevel`-Filter und Kopfzeilen-Nummer. */
	private loadRealEstate(realEstateContact: RealEstateContact): Observable<RealEstate | null> {
		return this.realEstateService
			.getRealEstate(this.areaOf(realEstateContact), realEstateContact.realEstateId)
			.pipe(catchError(() => of(null as RealEstate)));
	}

	private openPersonEditorDialog(
		entryContext: PersonEditorEntryContext,
		realEstateContact: RealEstateContact,
		tenantUnit: TenantUnitDetail,
		realEstate: RealEstate | null,
		uviReceptionRights: UviReceptionRights,
		isPropertyManagement: boolean
	): void {
		const dialogData: PersonEditorDialogData = {
			entryContext,
			...this.editorDataFactory.buildAddData(
				this.buildEditorTabContext(realEstateContact, tenantUnit, realEstate, uviReceptionRights, isPropertyManagement)
			),
		};

		this.blurActiveElement();
		const dialogRef = this.dialog.open(PersonEditorDialogComponent, {
			width: 'min(76rem, 96vw)',
			minWidth: '0',
			maxWidth: '96vw',
			maxHeight: '95vh',
			panelClass: 'person-editor-dialog-panel',
			autoFocus: false,
			restoreFocus: false,
			data: dialogData,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result?.saved) {
					this.syncBillingsAndRoute(realEstateContact, tenantUnit);
				}
			});
	}

	/** Gleicher Editor-Vertrag wie im Personen-Tab, nur aus der Schnellzugriff-Auswahl aufgebaut. */
	private buildEditorTabContext(
		realEstateContact: RealEstateContact,
		tenantUnit: TenantUnitDetail,
		realEstate: RealEstate | null,
		uviReceptionRights: UviReceptionRights,
		isPropertyManagement: boolean
	): EditorTabContext {
		const availableRoleTypes = this.computePersonRoleTypes(realEstate);
		const hideTenants = !availableRoleTypes.includes(UiRoleType.TENANT);
		const { roleModels, availablePeriodsByRoleType } = this.roleModelService.buildRoleModels(tenantUnit, hideTenants);

		return {
			realEstate,
			tenantUnit,
			realEstateArea: this.areaOf(realEstateContact),
			realEstateId: realEstateContact.realEstateId,
			tenantUnitId: tenantUnit.id,
			roleModels,
			availablePeriodsByRoleType,
			availableRoleTypes,
			// Berechtigungen lädt nur der Personen-Tab je Rolle nach; ohne sie bietet der Editor uVI „Portal" nicht an.
			permissions: {},
			uviReceptionRights,
			isPropertyManagement,
		};
	}

	/** Wie im Personen-Tab: Mieter werden ab SyncLevel 0/1 im führenden System gepflegt, also nicht angeboten. */
	private computePersonRoleTypes(realEstate: RealEstate | null): RoleType[] {
		const allRoleTypes = Object.values(UiRoleType).filter((roleType) => roleType !== UiRoleType.VACANCY);
		const syncLevel = realEstate?.syncLevel;

		if (syncLevel === SyncLevelEnum.LEVEL_0 || syncLevel === SyncLevelEnum.LEVEL_1) {
			return allRoleTypes.filter((roleType) => roleType !== UiRoleType.TENANT);
		}

		return allRoleTypes;
	}

	private areaOf(realEstateContact: RealEstateContact): number {
		return realEstateContact.area ?? this.sessionService.effectiveArea;
	}

	private syncBillingsAndRoute(realEstateContact: RealEstateContact, tenantUnit: TenantUnitDetail): void {
		this.billingHelper
			.syncRealEstateBillings(realEstateContact.area, realEstateContact.realEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.confirmRouteToTenantUnitDialog(realEstateContact, tenantUnit);
			});
	}

	private blurActiveElement(): void {
		if (typeof document === 'undefined') {
			return;
		}

		const activeElement = document.activeElement;
		if (activeElement instanceof HTMLElement) {
			activeElement.blur();
		}
	}

	confirmRouteToTenantUnitDialog(realEstateContact: RealEstateContact, tenantUnit: TenantUnitDetail) {
		if (!realEstateContact?.realEstateId || !tenantUnit?.id) {
			return;
		}
		const confirmRouteToTenantUnitDialog = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		confirmRouteToTenantUnitDialog.componentInstance.titleMessage = this.translocoService.translate('quickActions.confirmTenantChangeRouteToTenantUnit');

		confirmRouteToTenantUnitDialog
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((confirmRouteDialogResult) => {
				if (confirmRouteDialogResult) {
					sessionStorage.setItem('selectedRealEstateArea', String(realEstateContact.area));
					sessionStorage.setItem('selectedRealEstateID', realEstateContact.realEstateId);
					this.router.navigate([`realestates/${realEstateContact.realEstateId}/tenantunits/${tenantUnit.id}`]);
				}
			});
	}

	uploadDocument() {
		this.router.navigate(['document-transfer']);
	}

	changeUviPermission() {
		this.openSelectRealEstateAndTenantUnitDialog((realEstateContact, tenantUnit) => {
			sessionStorage.setItem('selectedRealEstateArea', String(realEstateContact.area));
			sessionStorage.setItem('selectedRealEstateID', realEstateContact.realEstateId);
			this.router.navigate([`realestates/${realEstateContact.realEstateId}/tenantunits/${tenantUnit.id}`]);
		});
	}
}
